const TRANSLATION_CACHE_KEY = 'rio-translation-cache-v1'
const TRANSLATION_TIMEOUT_MS = 7000
const TRANSLATION_CACHE_LIMIT = 1000
const TRANSLATION_WARN_LIMIT = 20
const WOWHEAD_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'

const warnedProviders = new Set()
let lastTranslationProvider = null

function warnProvider(provider, error) {
  const key = `${provider}:${error?.message || error}`
  if (warnedProviders.has(key) || warnedProviders.size >= TRANSLATION_WARN_LIMIT) return
  warnedProviders.add(key)
  console.warn(`Translation provider failed: ${provider}`, error)
}

function emitTranslationProvider(provider) {
  if (!provider?.label || provider.label === lastTranslationProvider?.label) return
  lastTranslationProvider = provider
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rio:translation-provider', {
      detail: provider,
    }))
  }
}

function isSupportedTarget(targetLang) {
  return ['en', 'uk', 'ru'].includes(targetLang)
}

function hashText(value = '') {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

function cacheKey(text, targetLang) {
  return `${targetLang}:${hashText(text)}`
}

function readCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY) || '{}')
    return cache && typeof cache === 'object' ? cache : {}
  } catch {
    return {}
  }
}

function writeCache(cache) {
  try {
    const entries = Object.entries(cache)
    const limited = entries.length > TRANSLATION_CACHE_LIMIT
      ? Object.fromEntries(entries.slice(entries.length - TRANSLATION_CACHE_LIMIT))
      : cache
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(limited))
  } catch {
    // Translation cache is best-effort only.
  }
}

function getCachedTranslation(text, targetLang) {
  const cache = readCache()
  const entry = cache[cacheKey(text, targetLang)]
  if (typeof entry === 'string') {
    return entry.trim()
      ? {
        text: entry,
        provider: {
          key: 'local-cache',
          label: 'Local cache',
          cached: true,
        },
      }
      : null
  }

  if (entry && typeof entry === 'object' && typeof entry.text === 'string' && entry.text.trim()) {
    return {
      text: entry.text,
      provider: {
        key: entry.provider || 'local-cache',
        label: entry.providerLabel || 'Local cache',
        cached: true,
      },
    }
  }

  return null
}

function setCachedTranslation(text, targetLang, translatedText, provider = {}) {
  if (!text || !translatedText || translatedText === text) return
  const cache = readCache()
  cache[cacheKey(text, targetLang)] = {
    text: translatedText,
    provider: provider.key || '',
    providerLabel: provider.label || '',
  }
  writeCache(cache)
}

async function requestWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), TRANSLATION_TIMEOUT_MS)

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

async function requestWorkerTranslation(text, targetLang) {
  const response = await requestWithTimeout(`${WOWHEAD_WORKER_URL}/translate`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text,
      targetLang,
    }),
  })

  if (!response.ok) {
    throw new Error(`Translation worker failed: ${response.status}`)
  }

  const data = await response.json()
  const translated = String(data.translatedText || '').trim()
  return {
    text: translated || text,
    provider: {
      key: data.provider || 'translation-worker',
      label: data.providerLabel || 'Translation worker',
      cached: Boolean(data.cached),
    },
  }
}

export async function translateText(text, targetLang) {
  const sourceText = String(text || '').trim()
  if (!sourceText) return ''
  if (targetLang === 'en') {
    emitTranslationProvider({ key: 'original', label: 'Original text' })
    return sourceText
  }
  if (!isSupportedTarget(targetLang)) return sourceText

  try {
    const cached = getCachedTranslation(sourceText, targetLang)
    if (cached) {
      emitTranslationProvider(cached.provider)
      return cached.text
    }

    const result = await requestWorkerTranslation(sourceText, targetLang)
    emitTranslationProvider(result.provider)
    if (result.text && result.text !== sourceText) {
      setCachedTranslation(sourceText, targetLang, result.text, result.provider)
    }
    return result.text || sourceText
  } catch (error) {
    warnProvider('translateText', error)
    emitTranslationProvider({ key: 'original', label: 'Original text' })
    return sourceText
  }
}

export function getLastTranslationProvider() {
  return lastTranslationProvider
}

export async function mapWithConcurrency(items, limit, asyncMapper) {
  const results = new Array(items.length)
  let nextIndex = 0
  const workerCount = Math.min(Math.max(limit, 1), items.length)

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      results[currentIndex] = await asyncMapper(items[currentIndex], currentIndex)
    }
  }))

  return results
}

export async function translatePost(post, targetLang) {
  if (targetLang === 'en') return post

  try {
    const title = await translateText(post.title, targetLang)
    const summary = await translateText(post.summary, targetLang)

    return {
      ...post,
      title,
      summary,
      originalTitle: post.title,
      originalSummary: post.summary,
    }
  } catch (error) {
    warnProvider('translatePost', error)
    return post
  }
}

export async function translatePosts(posts, targetLang) {
  if (targetLang === 'en') return posts
  if (!Array.isArray(posts) || !posts.length) return posts

  try {
    return await mapWithConcurrency(posts, 3, (post) => translatePost(post, targetLang))
  } catch (error) {
    warnProvider('translatePosts', error)
    return posts
  }
}
